import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { RotatingLines } from "react-loader-spinner";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import { formatDateFromUnixTimestamp, xReqId } from "../../utils";

const TransferEventsPage = () => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [errorDesc, setErrorDesc] = useState("Κάτι έχει πάει στραβά. Παρακαλώ δοκιμάστε αργότερα.");
	const [listEvents, setListEvents] = useState([]);

	useDocumentTitle("Διαχείριση μεταβιβάσεων");

	const fetchEvents = useCallback(async () => {
		setLoading(true);
		const res = await fetch("/api/ticket-office/transfer-events", {
			method: "GET",
			credentials: "include",
			headers: {
				"Content-Type": "application/json",
				"x-request-id": xReqId,
			},
		});

		try {
			if (res.ok) {
				const data = await res.json();
				setListEvents(data);
				setLoading(false);
			} else {
				throw res;
			}
		} catch (error) {
			const errorResponse = await error.text();
			setError(true);
			setLoading(false);
			try {
				const json = JSON.parse(errorResponse);
				const appError = json?.exceptions?.errors?.[0]?.errorDescription;
				setErrorDesc(appError);
			} catch {}
		}
	}, []);

	useEffect(() => {
		fetchEvents();
	}, [fetchEvents]);

	const seasonalEvents = listEvents?.filter((event) => event.seasonal);
	const nonSeasonalEvents = listEvents?.filter((event) => !event.seasonal);

	const defaultFormValues = {};

	const setDefaultValues = (event) => {
		if (event.seasonal) {
			defaultFormValues[`allowTransfer-${event.eventId}`] = event.allowTransfer ?? false;
			defaultFormValues[`maxTransfers-${event.eventId}`] = event.maxTransfers ?? 5;
			defaultFormValues[`transferMinuteLimit-${event.eventId}`] = event.transferMinuteLimit ?? 2;
			defaultFormValues[`resetTransfersAfterEvent-${event.eventId}`] = event.minToAutoRevertTransferredTicket !== null;
			// defaultFormValues[`minToAutoRevertTransferredTicket-${ticket.eventId}`] =
			//   ticket.minToAutoRevertTransferredTicket ?? undefined;
		} else defaultFormValues[`nonSeasonalTicket-${event.eventId}`] = !event.ignoreTransfers;
	};

	seasonalEvents?.forEach((event) => {
		setDefaultValues(event);
	});

	nonSeasonalEvents?.forEach((event) => {
		setDefaultValues(event);
	});

	const { register, formState, watch, reset, setValue } = useForm({
		mode: "all",
		defaultValues: defaultFormValues,
	});

	useEffect(() => {
		if (listEvents?.length) {
			const newValues = {};

			listEvents.forEach((event) => {
				if (event.seasonal) {
					newValues[`allowTransfer-${event.eventId}`] = event.allowTransfer;
					newValues[`maxTransfers-${event.eventId}`] = event.maxTransfers;
					newValues[`transferMinuteLimit-${event.eventId}`] = event.transferMinuteLimit;
					newValues[`resetTransfersAfterEvent-${event.eventId}`] = event.minToAutoRevertTransferredTicket !== null;
					// newValues[`minToAutoRevertTransferredTicket-${event.eventId}`] =
					//   event.minToAutoRevertTransferredTicket;
				} else {
					newValues[`nonSeasonalTicket-${event.eventId}`] = false;
				}
			});

			reset(newValues);
		}

	}, [listEvents, reset]);

	const hasEventChanges = (eventId) => {
		const event = seasonalEvents.find((e) => e.eventId === eventId);
		if (!event) return false;

		const currentState = {
			allowTransfer: Boolean(watch(`allowTransfer-${eventId}`)),
			maxTransfers: parseInt(watch(`maxTransfers-${eventId}`)) || 0,
			transferMinuteLimit: parseInt(watch(`transferMinuteLimit-${eventId}`)) || 0,
			resetTransfersAfterEvent: watch(`resetTransfersAfterEvent-${eventId}`) ?? false,
			// minToAutoRevertTransferredTicket:
			//   parseInt(watch(`minToAutoRevertTransferredTicket-${eventId}`)) || 0,
		};

		const initialState = {
			allowTransfer: Boolean(event.allowTransfer),
			maxTransfers: parseInt(event.maxTransfers) || 0,
			transferMinuteLimit: parseInt(event.transferMinuteLimit) || 0,
			resetTransfersAfterEvent:
				event.minToAutoRevertTransferredTicket !== undefined && event.minToAutoRevertTransferredTicket !== null,
			// minToAutoRevertTransferredTicket:
			//   parseInt(event.minToAutoRevertTransferredTicket) || 0,
		};

		return JSON.stringify(currentState) !== JSON.stringify(initialState);
	};

	const submitEventChanges = async (eventId) => {
		const isSeasonalEvent = seasonalEvents.some((e) => e.eventId === eventId);
		const eventIndex = listEvents.findIndex((e) => e.eventId === eventId);

		if (isSeasonalEvent && !window.confirm("Θέλετε σίγουρα να αποθηκεύσετε τις αλλαγές;")) {
			return;
		}

		const updatedEvent = {
			...listEvents[eventIndex],
			...(isSeasonalEvent
				? {
						allowTransfer: Boolean(watch(`allowTransfer-${eventId}`)),
						maxTransfers: parseInt(watch(`maxTransfers-${eventId}`)) || 0,
						transferMinuteLimit: parseInt(watch(`transferMinuteLimit-${eventId}`)) || 0,
						minToAutoRevertTransferredTicket: watch(`resetTransfersAfterEvent-${eventId}`) ? 0 : null,
						// minToAutoRevertTransferredTicket: watch(
						//   `resetTransfersAfterEvent-${eventId}`
						// )
						//   ? parseInt(
						//       watch(`minToAutoRevertTransferredTicket-${eventId}`)
						//     ) || 0
						//   : null,
					}
				: {
						ignoreTransfers: watch(`nonSeasonalTicket-${eventId}`),
					}),
		};

		try {
			const res = await fetch("/api/ticket-office/transfer-events", {
				method: "PUT",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					"x-request-id": xReqId,
				},
				body: JSON.stringify(updatedEvent),
			});

			if (res.ok) {
				const responseEvent = await res.json();
				setListEvents((prev) => {
					const newList = [...prev];
					newList[eventIndex] = responseEvent;
					setDefaultValues(responseEvent);
					return newList;
				});

				reset(defaultFormValues);
			} else {
				throw res;
			}
		} catch (error) {
			const errorResponse = await error.text();
			const genericErrorMessage =
				"Προέκυψε σφάλμα συστήματος. Παρακαλώ δοκιμάστε αργότερα και εάν το πρόβλημα εξακολουθεί επικοινωνήστε με την ομάδα υποστήριξης.";
			try {
				const json = JSON.parse(errorResponse);
				const appError = json?.exceptions?.errors?.[0]?.errorDescription;
				toast.error(appError ?? genericErrorMessage);
			} catch {
				toast.error(errorResponse);
			}
		}
	};

	if (loading) {
		return (
			<MainContainer>
				<div className="col-lg-12 d-flex flex-fill justify-content-center align-items-center">
					<output aria-live="polite" aria-label="Γίνεται φόρτωση των ρυθμίσεων μεταβίβασης" className="text-center">
						<span className="visually-hidden">Γίνεται φόρτωση των ρυθμίσεων μεταβίβασης.</span>
						<RotatingLines
							visible={true}
							height="96"
							width="96"
							color="grey"
							strokeWidth="5"
							strokeColor="#003476"
							animationDuration="0.75"
							ariaLabel="Γίνεται φόρτωση των ρυθμίσεων μεταβίβασης"
						/>
					</output>
				</div>
			</MainContainer>
		);
	}

	const getBody = () => {
		if (error) {
			return (
				<div className="p-4 border bg-body-tertiary border-danger rounded mb-4 col-7">
					<p className="mb-0">{errorDesc}</p>
				</div>
			);
		}

		if (listEvents.length === 0) {
			return (
				<div className="p-4 border bg-body-tertiary border-primary rounded mb-4 col-7">
					<p className="mb-0">Δεν βρέθηκαν αγώνες για διαχείριση μεταβιβάσεων.</p>
				</div>
			);
		}

		return (
			<div className="container mt-4">
				<div className="position-relative">
					<h1 className="mb-4">Διαχείριση μεταβιβάσεων</h1>
					<h2>Εισιτήρια διαρκείας</h2>
					<div className="accordion" id="seasonalTicketsAccordion">
						{seasonalEvents.map((event) => (
							<div
								className="accordion-item mb-5"
								key={event.eventId}
								style={{
									borderTopLeftRadius: "0.375rem",
									borderTopRightRadius: "0.375rem",
									borderBottomRightRadius: "0.375rem",
									borderBottomLeftRadius: "0.375rem",
									borderTop: "1px solid #dee2e6",
									borderBottom: "1px solid #dee2e6",
								}}
							>
								<div className="row">
									<div className="col-12">
										<div className="card border-0">
											<fieldset
												className="card-body border-0 p-3 m-0"
												aria-labelledby={`heading-${event.eventId}`}
											>
												<legend className="visually-hidden">Ομάδα ρυθμίσεων μεταβίβασης</legend>
												<div className="d-flex justify-content-between align-items-center">
													<h3 id={`heading-${event.eventId}`} className="accordion-header mb-0">
														{event.eventDescription}
													</h3>
													<div className="form-check form-switch form-check-reverse m-0">
															<input
																type="checkbox"
																id={`switch-allowTransfer-${event.eventId}`}
																className="form-check-input"
																{...register(`allowTransfer-${event.eventId}`)}
															/>
														<label className="form-check-label" htmlFor={`switch-allowTransfer-${event.eventId}`}>
															Ενεργοποίηση μεταβιβάσεων
														</label>
													</div>
												</div>

												{/* Submit Button */}
												<div className="mt-3">
													<div className="container">
														<div className="row mb-3">
															<div className="col-12 d-flex justify-content-end">
																<button
																	type="button"
																	disabled={!hasEventChanges(event.eventId)}
																	className="btn btn-primary btn-lg"
																	onClick={() => {
																		submitEventChanges(event.eventId);
																	}}
																>
																	{formState.isSubmitting ? (
																		<div className="d-flex justify-content-center align-items-center">
																			<RotatingLines
																				visible={true}
																				color="grey"
																				strokeWidth="5"
																				strokeColor="white"
																				animationDuration="0.75"
																				ariaLabel="rotating-lines-loading"
																				wrapperStyle={{}}
																				wrapperClass=""
																			/>
																			<div>Αποθήκευση</div>
																		</div>
																	) : (
																		"Αποθήκευση"
																	)}
																</button>
															</div>
														</div>
													</div>
												</div>
											</fieldset>
										</div>
									</div>
								</div>
								<section
									id={`collapse-${event.eventId}`}
									className={`accordion-collapse collapse ${watch(`allowTransfer-${event.eventId}`) ? "show" : ""}`}
									aria-labelledby={`heading-${event.eventId}`}
									data-bs-parent="#seasonalTicketsAccordion"
								>
									<div className="accordion-body">
										<div className="row mb-3">
											<div className="col-9">
												<label className="fw-medium" htmlFor={`maxTransfers-${event.eventId}`}>
													Μέγιστος επιτρεπτός αριθμός μεταβιβάσεων
												</label>
											</div>
											<div className="col-3">
												<input
													type="number"
													id={`maxTransfers-${event.eventId}`}
													placeholder="0"
													{...register(`maxTransfers-${event.eventId}`)}
													className={"form-control".concat(
														formState.errors[`maxTransfers-${event.eventId}`] ? " is-invalid" : "",
													)}
													min="1"
													step="1"
													onKeyUp={(event) => {
														if (event.target.value < 1) event.target.value = 1;
													}}
												/>
											</div>
										</div>
										<div className="row mb-3">
											<div className="col-9">
												<label className="fw-medium" htmlFor={`transferMinuteLimit-${event.eventId}`}>
													Διάστημα απενεργοποίησης μεταβιβάσεων <br />
													πριν την έναρξη του αγώνα (ώρες)
												</label>
											</div>
											<div className="col-3">
												<input
													type="number"
													id={`transferMinuteLimit-${event.eventId}`}
													placeholder="0"
													className="form-control"
													{...register(`transferMinuteLimit-${event.eventId}`)}
													min="1"
													max="6"
													step="1"
													onKeyUp={(event) => {
														if (event.target.value < 1) event.target.value = 1;
														else if (event.target.value > 6) event.target.value = 6;
													}}
												/>
											</div>
										</div>
										<div className="row mb-3">
											<div className="col-9">
												<div className="fw-medium">
													Επαναφορά μεταβιβάσεων μετά από κάθε αγώνα
												</div>
											</div>
											<div className="col-3 d-flex align-items-start gap-2">
												<div className="form-check form-switch form-check-reverse m-0">
													<input
														className="form-check-input"
														type="checkbox"
														id={`reset-transfers-after-event-switch-${event.eventId}`}
														{...register(`resetTransfersAfterEvent-${event.eventId}`)}
														onChange={(e) => {
															setValue(`resetTransfersAfterEvent-${event.eventId}`, e.target.checked);
															if (!e.target.checked) {
																setValue(`minToAutoRevertTransferredTicket-${event.eventId}`, "");
															}
														}}
													/>
													<label
														className="form-check-label visually-hidden"
														htmlFor={`reset-transfers-after-event-switch-${event.eventId}`}
													>
														Επαναφορά μεταβιβάσεων μετά από κάθε αγώνα
													</label>
												</div>
												{/* <input
                          type="number"
                          id={`reset-transfers-after-event-${event.eventId}`}
                          className="form-control"
                          {...register(
                            `minToAutoRevertTransferredTicket-${event.eventId}`,
                            {
                              value: "", // Initialize with empty string
                              onChange: (e) => {
                                setValue(
                                  `minToAutoRevertTransferredTicket-${event.eventId}`,
                                  e.target.value || ""
                                );
                              },
                            }
                          )}
                          disabled={
                            !watch(`resetTransfersAfterEvent-${event.eventId}`)
                          }
                        /> */}
											</div>
										</div>
									</div>
								</section>
							</div>
						))}
					</div>
					<h2>Πρόγραμμα αγώνων</h2>
					{nonSeasonalEvents.map((event) => (
						<div className="row mt-4" key={event.eventId}>
							<div className="col-12 mb-3">
								<fieldset
									className="card border-0 p-0 m-0"
									aria-labelledby={`nonseasonal-heading-${event.eventId}`}
								>
									<legend className="visually-hidden">Ομάδα ρύθμισης μεταβίβασης</legend>
									<div className="card-body d-flex justify-content-between align-items-center">
										<div>
											<h5 id={`nonseasonal-heading-${event.eventId}`} className="card-title">
												{event.eventDescription}
											</h5>
											<div className="card-text">
												{formatDateFromUnixTimestamp(event.eventDateTime)}{" "}
												{formatDateFromUnixTimestamp(event.eventDateTime, true)}
											</div>
										</div>
										<div className="form-check form-switch form-check-reverse m-0">
												<input
													className="form-check-input"
													type="checkbox"
													id={`switch-${event.eventId}`}
													{...register(`nonSeasonalTicket-${event.eventId}`, {
														value: !event.ignoreTransfers,
													})}
													defaultChecked={!event.ignoreTransfers}
													onChange={() => {
														if (window.confirm("Θέλετε σίγουρα να συνεχίσετε;")) {
															submitEventChanges(event.eventId);
														} else {
															// Reset to initial value if cancelled
															setValue(`nonSeasonalTicket-${event.eventId}`, !event.ignoreTransfers);
														}
													}}
												/>
											<label className="form-check-label" htmlFor={`switch-${event.eventId}`}>
												Ενεργοποίηση μεταβιβάσεων
											</label>
											</div>
										</div>
								</fieldset>
							</div>
						</div>
					))}
				</div>
			</div>
		);
	};

	return (
		<MainContainer>
			<div className="col-lg-12">
				<Link to="/ticket-office/choose-user" className="btn btn-back mb-4">
					<svg className="govgr-svg-icon--caret govgr-svg-icon" focusable="false" viewBox="0 0 24 24" aria-hidden="true">
						<path d="M18,22V2L6,12L18,22z"></path>
					</svg>
					Πίσω
				</Link>
				{getBody()}
			</div>
		</MainContainer>
	);
};

export default TransferEventsPage;
